export type Frequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export const FREQUENCIES: readonly Frequency[] = ['daily', 'weekly', 'monthly', 'yearly'];

export type RuleStatus = 'active' | 'paused' | 'ended';
export type PausedReason =
  | 'user'
  | 'destination_unavailable'
  | 'source_unavailable'
  | 'stopped'
  | 'needs_review'
  | 'repeated_failures'
  | 'imported';

/** A recurrence rule as the API returns it (dates are local to `timezone`). */
export interface RecurrenceRule {
  id: string;
  source_card_id: string;
  board_id: string;
  /** `null` once the destination column was deleted for good (the rule is then paused). */
  column_id: string | null;
  frequency: Frequency;
  interval: number;
  /** 0 = Monday ... 6 = Sunday; weekly only. */
  weekdays: number[];
  start_date: string;
  /** `HH:MM:SS`: when the new card is created (local time). */
  time_of_day: string;
  timezone: string;
  until_date: string | null;
  status: RuleStatus;
  paused_reason: PausedReason | null;
  next_occurrence_date: string | null;
  next_run_at: string | null;
  last_generated_at: string | null;
  generated_count: number;
  version: number;
  failure_count: number;
  last_error: string | null;
  /** Upcoming individual changes (moved, edited or cancelled occurrences). */
  exception_count: number;
  /** The series template: what future cards contain (dates follow the schedule). */
  title: string | null;
  start_time: string | null;
  end_time: string | null;
  length_days: number | null;
  is_event: boolean;
  has_deadline: boolean;
  /** Events: generated cards may become overdue after their end (default off; always sent). */
  overdue_enabled?: boolean;
}

/** What to do with upcoming individual changes when the whole series is edited. */
export type ExceptionPolicy = 'preserve' | 'reset';

/** Edits of the series template on top of the card snapshot (absent = unchanged). */
export interface TemplateEdit {
  title?: string;
  /** `HH:MM`. */
  start_time?: string;
  end_time?: string;
  length_days?: number;
  overdue_enabled?: boolean;
}

/** Changes to ONE occurrence; absent fields follow the series. */
export interface OccurrenceOverrides {
  title?: string | null;
  start_date?: string | null;
  start_time?: string | null;
  end_date?: string | null;
  end_time?: string | null;
  clear_start_time?: boolean;
  clear_end_time?: boolean;
}

export interface OccurrenceException {
  id: string;
  rule_id: string;
  /** The occurrence's schedule date: its stable identity. */
  original_date: string;
  cancelled: boolean;
  overrides: OccurrenceOverrides | null;
  version: number;
}

export interface RecurrencePreview {
  exception_count: number;
  mapped: { original_date: string; new_date: string }[];
  conflicts: { original_date: string; reason: string }[];
  next_dates: string[];
}

/** `GET /cards/{id}/recurrence`: the card's own rule, or the series it was generated by. */
export interface RecurrenceInfo {
  role: 'source' | 'instance' | null;
  rule: RecurrenceRule | null;
  original_date?: string | null;
}

/** The editable form state (text values, like the other date drafts). */
export interface RecurrenceDraft {
  frequency: Frequency;
  interval: number;
  weekdays: number[];
  startDate: string;
  /** `HH:MM`, empty = 00:00. */
  timeOfDay: string;
  /** How the SERIES ends (not an event's own end): never, or on `untilDate`. */
  endMode: 'never' | 'date';
  /** Last day of the series (inclusive); used only when `endMode` is `date`. */
  untilDate: string;
}

/** Scope of a bulk operation on future recurrence occurrences. */
export type RecurrenceBulkScope = 'following' | 'series';

export interface RecurrenceBulkPreviewInput {
  scope: RecurrenceBulkScope;
  /** Original schedule slot, required for "following". */
  original_date?: string;
  expected_version?: number;
}

export interface RecurrenceBulkPreview {
  scope: RecurrenceBulkScope;
  from_date: string;
  rule_version: number;
  rule_status: RuleStatus;
  materialized_cards: number;
  exceptions: number;
  next_schedule_dates: string[];
  no_explicit_end: boolean;
}

export interface RecurrenceBulkDeleteInput {
  scope: RecurrenceBulkScope;
  original_date?: string;
  expected_version: number;
  expected_from_date: string;
  expected_materialized_cards: number;
  expected_exceptions: number;
}

export interface RecurrenceBulkDeleteResult {
  scope: RecurrenceBulkScope;
  from_date: string;
  materialized_cards: number;
  exceptions_removed: number;
  rule_status: RuleStatus;
  rule_version: number;
}

export interface RecurrenceBulkEditPreviewInput {
  scope: 'following' | 'series';
  original_date?: string;
  expected_version: number;
  template: TemplateEdit;
}

export interface RecurrenceBulkEditPreview {
  scope: 'following' | 'series';
  from_date: string;
  rule_version: number;
  materialized_cards: number;
  fully_editable: number;
  partially_editable: number;
  protected: number;
  title_updates: number;
  deadline_updates: number;
  title_changed: boolean;
  deadline_changed: boolean;
}

export interface RecurrenceBulkEditInput extends RecurrenceBulkEditPreviewInput {
  expected_from_date: string;
  expected_materialized_cards: number;
  expected_fully_editable: number;
  expected_partially_editable: number;
  expected_protected: number;
  expected_title_updates: number;
  expected_deadline_updates: number;
}

export interface RecurrenceBulkEditResult {
  scope: 'series';
  from_date: string;
  materialized_cards: number;
  title_updates: number;
  deadline_updates: number;
  rule_version: number;
}
